// CONTRACT:C1-MAP-CLIENT.1.0
// COP — URL STATE CAPTURE: shareable "look at this one" deep links.
//
// Google-Maps-style: the operator's current VIEW (camera) + SELECTION is reflected
// into the URL as a HASH FRAGMENT, and loading such a URL restores that view. So an
// operator can copy the URL and send a peer "hey, look at this one."
//
// Format:  #@<lat>,<lng>,<zoom>z  + optional  &sel=<entityId>&mode=<markerMode>&view=<2d|2.5d>
// Example: #@26.58,56.38,9.5z&sel=scop:object:abc&mode=coalition&view=2.5d
//
// WHY A HASH FRAGMENT, NOT QUERY PARAMS (load-bearing): a fragment is NEVER sent to
// the server — it stays client-side, so the selected-entity id (which can be
// classification-sensitive — GLOMAR) never lands in a server log, a Referer header,
// or a proxy access log. Query params would leak it. This module only ever writes the
// state into the fragment.
//
// ⭐ FAIL-CLOSED BY CONSTRUCTION: restoring `sel=<id>` goes through the SAME
// handle.select() → onSelect → PDP/KAS path as a mouse click. This module holds NO
// decision authority (I13: presentation/navigation only) — it never gates visibility,
// it only replays a navigation. An uncleared recipient who opens the link logs in as
// THEMSELVES and gets Glomar/sealed exactly as if they had browsed there: the link
// GRANTS NOTHING. If the entity id is not in the visible set for this viewer,
// handle.select() silently no-ops (selection stays null) — never an error, never a
// reveal of existence. See startSelRestore() below.
//
// SHARER-RESPONSIBILITY (accepted MVP tradeoff): a TS entity id in a shared URL leaks
// that entity's EXISTENCE and (via the id-scoped fly-to) its rough LOCATION to the
// recipient, even though its CONTENT stays sealed to them. This is the same tradeoff
// as pasting a set of coordinates into an email — the URL itself IS the share; there
// is no server-side share infrastructure. Sharers own who they send links to.

import type { CopHandle, CopStatus } from "./cop.ts";

/** A parsed/parseable URL view-state. lat/lng/zoom are the camera; sel/mode/view are
 *  optional overlays. This is the pure data the parse/format functions round-trip. */
export interface UrlViewState {
  lat: number;
  lng: number;
  zoom: number;
  /** Selected entity id (opaque; goes through the fail-closed select path on restore). */
  sel?: string;
  /** Marker/symbology carrier (overrides the persisted default when present). */
  mode?: "milsym" | "dot" | "coalition";
  /** 2D / 2.5D camera view mode (overrides the boot default when present). */
  view?: "2d" | "2.5d";
}

// Coordinate/zoom rounding — short URLs, stable round-trips (Google-Maps parity).
const LATLNG_DP = 5; // ~1.1 m at the equator — plenty for "look at this one"
const ZOOM_DP = 2;

// Sane ranges. lat/lng/zoom are CLAMPED (not rejected) so a mildly-out-of-range or
// hand-edited fragment still restores a VALID camera instead of failing the whole link.
const LAT_MIN = -90, LAT_MAX = 90;
const LNG_MIN = -180, LNG_MAX = 180;
const ZOOM_MIN = 0, ZOOM_MAX = 24;

function clamp(n: number, lo: number, hi: number): number {
  return n < lo ? lo : n > hi ? hi : n;
}

/** Round to `dp` decimals WITHOUT floating-point tail noise, and strip trailing zeros
 *  (8.00 → 8, 9.50 → 9.5). Number(toFixed) is the clean way. */
function round(n: number, dp: number): number {
  return Number(n.toFixed(dp));
}

/** Format a view-state into the hash fragment (leading '#').  Pure. */
export function formatHash(s: UrlViewState): string {
  const lat = round(clamp(s.lat, LAT_MIN, LAT_MAX), LATLNG_DP);
  const lng = round(clamp(s.lng, LNG_MIN, LNG_MAX), LATLNG_DP);
  const zoom = round(clamp(s.zoom, ZOOM_MIN, ZOOM_MAX), ZOOM_DP);
  let hash = `#@${lat},${lng},${zoom}z`;
  const params: string[] = [];
  // encodeURIComponent so an id containing a structural char (& # =) can never break
  // the parse. Colons survive as %3A on the wire and decode back — functionally
  // identical to the readable `scop:object:abc` form (which also still parses if a
  // human hand-types it).
  if (s.sel) params.push(`sel=${encodeURIComponent(s.sel)}`);
  if (s.mode) params.push(`mode=${s.mode}`);
  if (s.view) params.push(`view=${s.view}`);
  if (params.length > 0) hash += `&${params.join("&")}`;
  return hash;
}

/** Parse a hash fragment into a view-state, or null if it is not a view fragment.
 *
 *  TOLERANT by design: a missing '#'/'@', a non-view fragment (#foo), an empty string,
 *  a wrong field count, or a non-finite core number all return null (→ the controller
 *  no-ops, leaving persisted/boot behavior intact). Unknown/invalid mode/view/sel
 *  values are simply dropped, not fatal. Out-of-range lat/lng/zoom are CLAMPED. Pure. */
export function parseHash(hash: string): UrlViewState | null {
  if (typeof hash !== "string" || hash.length === 0) return null;
  const raw = hash.startsWith("#") ? hash.slice(1) : hash;
  if (!raw.startsWith("@")) return null; // not a view fragment — leave everything alone
  const body = raw.slice(1);
  const amp = body.indexOf("&");
  const core = amp === -1 ? body : body.slice(0, amp);
  const rest = amp === -1 ? "" : body.slice(amp + 1);

  // core = "lat,lng,zoom" with a trailing 'z' on the zoom (Google-Maps style).
  const coreNoZ = /[zZ]$/.test(core) ? core.slice(0, -1) : core;
  const parts = coreNoZ.split(",");
  if (parts.length !== 3) return null;
  // Reject empty/whitespace parts (Number("") === 0 would otherwise pass silently).
  if (parts.some((p) => p.trim() === "")) return null;
  const lat = Number(parts[0]);
  const lng = Number(parts[1]);
  const zoom = Number(parts[2]);
  if (!Number.isFinite(lat) || !Number.isFinite(lng) || !Number.isFinite(zoom)) return null;

  const out: UrlViewState = {
    lat: clamp(lat, LAT_MIN, LAT_MAX),
    lng: clamp(lng, LNG_MIN, LNG_MAX),
    zoom: clamp(zoom, ZOOM_MIN, ZOOM_MAX),
  };

  // Optional overlays. URLSearchParams decodes percent-encoding for us.
  const params = new URLSearchParams(rest);
  const sel = params.get("sel");
  if (sel) out.sel = sel;
  const mode = params.get("mode");
  if (mode === "milsym" || mode === "dot" || mode === "coalition") out.mode = mode;
  const view = params.get("view");
  if (view === "2d" || view === "2.5d") out.view = view;
  return out;
}

// ---------------------------------------------------------------------------
// The controller — wires READ (URL → state, once on load) + WRITE (state → URL,
// debounced) given a CopHandle. All URL/history/timer access goes through an
// injectable UrlStateEnv so the whole controller is deterministically testable
// headless (no DOM), and so SSR / tsx checks never throw.
// ---------------------------------------------------------------------------

const WRITE_DEBOUNCE_MS = 300; // debounce map moveend so a pan/zoom writes once it settles
const SEL_POLL_MS = 250; // how often to retry the deferred selection restore
const SEL_RESTORE_DEADLINE_MS = 12_000; // give up the sel restore after this (fail-closed, silent)
const SEL_MAX_ATTEMPTS = 24; // hard cap on select() retries (belt-and-suspenders vs. the deadline)

/** The seam the controller uses for URL + timers. Real impl reads window/history/
 *  location; tests inject fakes. Every method is guarded for a missing DOM. */
export interface UrlStateEnv {
  getHash(): string;
  /** Write the fragment via replaceState (NO history entry — no back-button spam). */
  setHash(hash: string): void;
  /** The full current href (origin + path + query + fragment) — the shareable URL. */
  href(): string;
  now(): number;
  setTimeout(cb: () => void, ms: number): number;
  clearTimeout(id: number): void;
  setInterval(cb: () => void, ms: number): number;
  clearInterval(id: number): void;
}

export interface UrlStateController {
  /** Flush the current state to the URL immediately and return the full shareable
   *  href — used by the ⌘K "Copy link to this view" command so the copied URL is never
   *  a debounce-tick stale. */
  getShareUrl(): string;
  /** Tear down all subscriptions + pending timers. */
  dispose(): void;
}

/** Build the default browser-backed env, fully guarded for headless/SSR. When there is
 *  no window the timer factories return 0 and never fire, so the controller is inert
 *  (exactly what we want under tsx checks / SSR). */
export function defaultUrlStateEnv(): UrlStateEnv {
  const hasWindow = typeof window !== "undefined";
  const hasHistory = typeof history !== "undefined";
  const hasLocation = typeof location !== "undefined";
  return {
    getHash: () => (hasLocation ? location.hash : ""),
    setHash: (hash: string) => {
      // replaceState (NOT pushState): update the URL WITHOUT a history entry. The hash
      // is a RELATIVE fragment, so path + query (?scenario= / ?mock=) are PRESERVED — we
      // only ever touch the fragment. GLOMAR: fragment state never hits server logs.
      if (!hasHistory || !hasLocation) return;
      try {
        history.replaceState(history.state, "", hash);
      } catch {
        /* replaceState can throw in exotic sandboxes — never fatal for navigation */
      }
    },
    href: () => (hasLocation ? location.href : ""),
    now: () => Date.now(),
    setTimeout: (cb, ms) => (hasWindow ? window.setTimeout(cb, ms) : 0),
    clearTimeout: (id) => {
      if (hasWindow) window.clearTimeout(id);
    },
    setInterval: (cb, ms) => (hasWindow ? window.setInterval(cb, ms) : 0),
    clearInterval: (id) => {
      if (hasWindow) window.clearInterval(id);
    },
  };
}

function selId(s: CopStatus | null): string | null {
  return s?.selected?.id ?? null;
}

/** Wire URL-state capture over a booted CopHandle. Call once after boot (AFTER the
 *  persisted display prefs are applied, so a hash OVERRIDES them). Returns a controller
 *  whose dispose() must run on teardown. */
export function createUrlStateController(
  handle: CopHandle,
  env: UrlStateEnv = defaultUrlStateEnv(),
): UrlStateController {
  let last: CopStatus | null = null;
  let disposed = false;
  let writeTimer: number | null = null;
  const unsubs: Array<() => void> = [];

  // ---- WRITE (state → URL) ----
  const buildState = (): UrlViewState | null => {
    const cam = handle.getCamera();
    if (!cam) return null; // headless / map not ready — nothing to reflect
    const st: UrlViewState = { lat: cam.lat, lng: cam.lng, zoom: cam.zoom };
    if (last) {
      if (last.selected) st.sel = last.selected.id;
      st.mode = last.markerMode;
      st.view = last.viewMode;
    }
    return st;
  };
  const writeNow = (): void => {
    writeTimer = null;
    const st = buildState();
    if (!st) return;
    const hash = formatHash(st);
    if (env.getHash() !== hash) env.setHash(hash); // skip no-op writes (no churn)
  };
  const scheduleWrite = (): void => {
    if (disposed) return;
    if (writeTimer !== null) env.clearTimeout(writeTimer);
    writeTimer = env.setTimeout(writeNow, WRITE_DEBOUNCE_MS);
  };

  // Status drives the write on URL-RELEVANT changes only (selection / marker / view).
  // trackCount ticks every feed frame; scheduling a write on those would starve the
  // debounce during a busy feed, so we filter to the fields the URL actually carries.
  unsubs.push(
    handle.onStatus((s) => {
      const prev = last;
      last = s;
      const relevant =
        prev === null ||
        selId(prev) !== selId(s) ||
        prev.markerMode !== s.markerMode ||
        prev.viewMode !== s.viewMode;
      if (relevant) scheduleWrite();
    }),
  );
  // Camera settle → reflect the new view (debounced).
  unsubs.push(handle.onMoveEnd(scheduleWrite));

  // ---- READ (URL → state), once, on construction ----
  const initial = parseHash(env.getHash());
  if (initial) {
    // Camera first: JUMP (no animation) to the restored view. Entities aren't needed
    // for the camera, so this happens immediately.
    handle.setCamera({ lat: initial.lat, lng: initial.lng, zoom: initial.zoom });
    // Then the display overlays — these OVERRIDE the persisted/boot defaults because a
    // hash is present (presentation only; never touches clearance).
    if (initial.mode) handle.setMarkerMode(initial.mode);
    if (initial.view) handle.setViewMode(initial.view);
    // Selection LAST, deferred until entities have painted.
    if (initial.sel) startSelRestore(initial.sel);
  }

  /** Restore a selection through the SAME onSelect → PDP/KAS path as a click, once the
   *  first feed frame has painted (entities must exist to resolve the id). FAIL-CLOSED:
   *  handle.select() resolves the id ONLY against this viewer's cleared-visible set, so
   *  an uncleared viewer's attempt no-ops (selection stays null) — identical to a
   *  non-existent object, never an error, never a reveal. We poll (rather than select on
   *  every status tick) to stay OUT of the status re-entrancy and to bound the effort. */
  function startSelRestore(id: string): void {
    const deadline = env.now() + SEL_RESTORE_DEADLINE_MS;
    let attempts = 0;
    const timer = env.setInterval(() => {
      if (disposed) {
        env.clearInterval(timer);
        return;
      }
      // Stop as soon as SOMETHING is selected: either our select() landed, or the user
      // clicked another object first — we defer to them and stop trying either way.
      if (last?.selected) {
        env.clearInterval(timer);
        return;
      }
      if (env.now() > deadline || attempts >= SEL_MAX_ATTEMPTS) {
        // Give up silently — the id was never in this viewer's visible set (uncleared /
        // unknown / not yet fed). No toast, no error: the link simply granted nothing.
        // CLEAN THE ORPHAN: rewrite the hash from the (unselected) current state so an
        // unresolvable sel= doesn't linger in the URL claiming a selection that isn't there
        // (else copying THIS url would propagate a dead sel=). The live persona-step-down path
        // already self-cleans via the selection-change status write; this handles the cold-boot
        // path, which never emits a deselection (nothing was ever selected). Presentation only.
        env.clearInterval(timer);
        writeNow();
        return;
      }
      // Only attempt once entities have painted; before that nothing could resolve.
      if ((last?.trackCount ?? 0) > 0) {
        attempts++;
        handle.select(id); // glomar-safe no-op if `id` isn't cleared-visible for this viewer
      }
    }, SEL_POLL_MS);
    unsubs.push(() => env.clearInterval(timer));
  }

  return {
    getShareUrl(): string {
      // Flush any pending debounced write NOW so the copied URL matches the live view.
      if (writeTimer !== null) {
        env.clearTimeout(writeTimer);
        writeTimer = null;
      }
      writeNow();
      return env.href();
    },
    dispose(): void {
      disposed = true;
      if (writeTimer !== null) {
        env.clearTimeout(writeTimer);
        writeTimer = null;
      }
      for (const u of unsubs) u();
      unsubs.length = 0;
    },
  };
}
